/**
 * Multi-Boolean Badge Component
 * Displays composite boolean states like open/closed/moving based on multiple boolean values
 */

import React from 'react';
import {
  Chip,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as OpenIcon,
  Cancel as ClosedIcon,
  SwapHoriz as MovingIcon,
  Error as ErrorIcon,
  QuestionMark as UnknownIcon,
  CheckCircle,
  Cancel,
  SwapHoriz,
} from '@mui/icons-material';

// Types
interface BooleanInput {
  name: string;
  value: boolean | null;
  description?: string;
}

interface StateDefinition {
  name: string;
  pattern: string; // e.g., "01" for open, "10" for closed, "00" for moving
  color: string;
  icon?: React.ReactNode;
  description?: string;
  priority?: number; // Higher priority states override lower ones
}

interface MultiBooleanBadgeProps {
  inputs: BooleanInput[];
  stateDefinitions: StateDefinition[];
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
  onClick?: () => void;
  variant?: 'filled' | 'outlined';
  title?: string; // Badge name/title for tooltip
  showLabel?: boolean; // Show label above badge
  labelText?: string; // Custom label text
  kksCode?: string; // KKS code for tooltip
}

// Default state definitions for common valve states
export const VALVE_STATES: StateDefinition[] = [
  {
    name: 'Open',
    pattern: '01',
    color: '#4caf50', // Green
    icon: <OpenIcon />,
    description: 'Valve is fully open',
    priority: 2,
  },
  {
    name: 'Closed',
    pattern: '10',
    color: '#f44336', // Red
    icon: <ClosedIcon />,
    description: 'Valve is fully closed',
    priority: 2,
  },
  {
    name: 'Moving',
    pattern: '00',
    color: '#ff9800', // Orange
    icon: <MovingIcon />,
    description: 'Valve is in motion',
    priority: 3, // Higher priority than open/closed
  },
  {
    name: 'Fault',
    pattern: '11',
    color: '#9c27b0', // Purple
    icon: <ErrorIcon />,
    description: 'Valve fault condition',
    priority: 4, // Highest priority
  },
];

// Default state definitions for pump states
export const PUMP_STATES: StateDefinition[] = [
  {
    name: 'Running',
    pattern: '10',
    color: '#4caf50', // Green
    icon: <CheckCircle />,
    description: 'Pump is running',
    priority: 2,
  },
  {
    name: 'Stopped',
    pattern: '01',
    color: '#757575', // Gray
    icon: <Cancel />,
    description: 'Pump is stopped',
    priority: 1,
  },
  {
    name: 'Starting',
    pattern: '11',
    color: '#ff9800', // Orange
    icon: <SwapHoriz />,
    description: 'Pump is starting',
    priority: 3,
  },
  {
    name: 'Fault',
    pattern: '00',
    color: '#f44336', // Red
    icon: <ErrorIcon />,
    description: 'Pump fault condition',
    priority: 4,
  },
];

// Default state definitions for door/gate states
export const DOOR_STATES: StateDefinition[] = [
  {
    name: 'Open',
    pattern: '10',
    color: '#4caf50', // Green
    icon: <OpenIcon />,
    description: 'Door/Gate is open',
    priority: 2,
  },
  {
    name: 'Closed',
    pattern: '01',
    color: '#2196f3', // Blue
    icon: <ClosedIcon />,
    description: 'Door/Gate is closed',
    priority: 2,
  },
  {
    name: 'Opening',
    pattern: '00',
    color: '#ff9800', // Orange
    icon: <MovingIcon />,
    description: 'Door/Gate is opening',
    priority: 3,
  },
  {
    name: 'Error',
    pattern: '11',
    color: '#f44336', // Red
    icon: <ErrorIcon />,
    description: 'Door/Gate error condition',
    priority: 4,
  },
];

export const MultiBooleanBadge: React.FC<MultiBooleanBadgeProps> = ({
  inputs,
  stateDefinitions,
  size = 'medium',
  showTooltip = true,
  onClick,
  variant = 'filled',
  title,
  showLabel = false,
  labelText,
  kksCode,
}) => {
  // Create current pattern from boolean inputs
  const currentPattern = inputs.map(input => {
    if (input.value === null || input.value === undefined) {
      return '?'; // Unknown state
    }
    return input.value ? '1' : '0';
  }).join('');

  // Find matching state definition
  const currentState = stateDefinitions
    .filter(state => {
      // Handle unknown values in pattern matching
      if (currentPattern.includes('?')) {
        return false; // Don't match if any value is unknown
      }
      return state.pattern === currentPattern;
    })
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))[0]; // Sort by priority

  // Fallback for unknown states
  const unknownState: StateDefinition = {
    name: 'Unknown',
    pattern: currentPattern,
    color: '#9e9e9e', // Gray
    icon: <UnknownIcon />,
    description: 'State cannot be determined',
    priority: 0,
  };

  const displayState = currentState || unknownState;

  // Generate tooltip content
  const tooltipContent = (
    <Box>
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {title}
        </Typography>
      )}
      {kksCode && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, color: '#1976d2' }}>
          KKS: {kksCode}
        </Typography>
      )}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
        Status: {displayState.name}
      </Typography>
      {displayState.description && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {displayState.description}
        </Typography>
      )}
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        Pattern: {currentPattern}
      </Typography>
      <Box sx={{ mt: 1 }}>
        {inputs.map((input, index) => (
          <Typography key={index} variant="caption" display="block">
            {input.name}: {input.value === null ? 'Unknown' : input.value ? 'True' : 'False'}
            {input.description && ` (${input.description})`}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  // Determine chip size
  const chipSize = size === 'large' ? 'medium' : 'small';
  const iconSize = size === 'large' ? 'medium' : 'small';

  const chip = (
    <Chip
      label={displayState.name}
      icon={displayState.icon as React.ReactElement}
      size={chipSize}
      variant={variant}
      onClick={onClick}
      sx={{
        backgroundColor: variant === 'filled' ? displayState.color : 'transparent',
        color: variant === 'filled' ? 'white' : displayState.color,
        borderColor: variant === 'outlined' ? displayState.color : undefined,
        fontWeight: 'bold',
        cursor: onClick ? 'pointer' : 'default',
        '& .MuiChip-icon': {
          color: variant === 'filled' ? 'white' : displayState.color,
        },
        '&:hover': onClick ? {
          opacity: 0.8,
          transform: 'scale(1.05)',
        } : {},
        transition: 'all 0.2s ease-in-out',
      }}
    />
  );

  // Determine label to display
  const displayLabel = labelText || (showLabel ? title : '');
  
  // Create badge with optional label
  const badgeWithLabel = (
    <Box sx={{ 
      display: 'inline-flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 0.5
    }}>
      {showLabel && displayLabel && (
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: size === 'large' ? '0.75rem' : '0.65rem',
            fontWeight: 'bold',
            color: 'text.secondary',
            textAlign: 'center',
            lineHeight: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          {displayLabel}
        </Typography>
      )}
      {chip}
    </Box>
  );

  if (showTooltip) {
    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Box sx={{ display: 'inline-block' }}>
          {badgeWithLabel}
        </Box>
      </Tooltip>
    );
  }

  return badgeWithLabel;
};

// Utility function to create state definitions from a configuration object
export const createStateDefinitions = (config: {
  [stateName: string]: {
    pattern: string;
    color: string;
    iconName?: string;
    description?: string;
    priority?: number;
  }
}): StateDefinition[] => {
  const iconMap: { [key: string]: React.ReactNode } = {
    open: <OpenIcon />,
    closed: <ClosedIcon />,
    moving: <MovingIcon />,
    error: <ErrorIcon />,
    unknown: <UnknownIcon />,
    check: <CheckCircle />,
    cancel: <Cancel />,
    swap: <SwapHoriz />,
  };

  return Object.entries(config).map(([name, def]) => ({
    name,
    pattern: def.pattern,
    color: def.color,
    icon: def.iconName ? iconMap[def.iconName] : undefined,
    description: def.description,
    priority: def.priority || 1,
  }));
};

// Hook for managing multi-boolean state
export const useMultiBooleanState = (
  tagValues: { [tagName: string]: boolean | null },
  tagOrder: string[]
) => {
  return React.useMemo(() => {
    return tagOrder.map(tagName => ({
      name: tagName,
      value: tagValues[tagName] ?? null,
    }));
  }, [tagValues, tagOrder]);
};

